import React from 'react';
import { motion } from 'motion/react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SocialShare } from '@/components/ui/SocialShare';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useRegion } from '@/contexts/RegionContext';
import { LazyImage } from '@/components/ui/LazyImage';

export function LensEmotionalResiliencePage() {
  const { t } = useTranslation();
  const { regionCode } = useRegion();

  const title = 'Emotional Resilience Lens | Stress Recovery & Adaptability | FIVE LENS';
  const description = 'Assess capacity to adapt, recover, and maintain clarity under strain. The Emotional Resilience lens maps stress recovery for coaching and team development.';

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: t('nav.framework', 'The Framework'), href: '/#framework' },
          { label: t('framework.lens3.title', 'Emotional Resilience') }
        ]} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-6 flex items-center gap-3">
            <span className="h-[1px] w-8 bg-rule-3"></span>
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">Lens 03</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-serif leading-tight mb-8 text-ink font-normal">
            Emotional Resilience
          </h1>
          
          <div className="prose prose-lg prose-brand max-w-none text-ink/70/80 leading-relaxed font-light">
            <p className="text-xl mb-4 font-normal text-ink">
              The Emotional Resilience lens measures an individual’s ability to remain effective under pressure. It focuses on the traits that influence how people respond to challenge, regulate their emotions, and sustain a constructive mindset in the face of uncertainty.
            </p>
            <p className="mb-8">
              Resilience is not simply about “pushing through” — it’s about staying flexible, emotionally grounded, and confident enough to take action. This lens offers a structured view of those underlying capacities and provides practical feedback to support growth.
            </p>
            
            <LazyImage 
              src="/resilience-DETAILED.webp" 
              alt="Emotional Resilience lens diagram showing capacity to adapt, recover, and maintain effectiveness under strain" 
              className="w-full max-w-md mx-auto my-12 object-contain mix-blend-multiply"
            />
            
            <h3 className="text-2xl font-serif text-ink mb-4">How This Lens Fits Into the FIVE LENS Framework</h3>
            <p className="mb-4">
              The Enneagram gives us insight into behavioural patterns, but not how people respond when those patterns are disrupted by stress. Emotional Resilience fills that gap by assessing how well someone can adapt, recover, and maintain clarity when under strain.
            </p>
            <p className="mb-8">
              This lens adds a vital dimension to the FIVE LENS model by revealing how inner stability influences outward behaviour. It’s especially important when coaching for leadership development, change readiness, or performance under pressure.
            </p>

            <h3 className="text-2xl font-serif text-ink mb-4">How It Supports Coaching & Organisational Development</h3>
            <p className="mb-4">
              Emotional Resilience plays a central role in personal and professional effectiveness. In coaching, it enables practitioners to assess how well a client can stay grounded and act with intention under stress — and where they might benefit from reframing or skill-building.
            </p>
            <p className="mb-8">
              In organisations, this lens supports resilience-building strategies, helps identify risk of burnout or reactivity, and provides practical data for leadership development, wellbeing programmes, and change management initiatives.
            </p>

            <div className="bg-white p-8 md:p-12 border border-ground-deep my-12">
              <h3 className="text-2xl font-serif text-ink mb-6">Core Components of Resilience</h3>
              <p className="mb-6">Within the FIVE LENS model, Emotional Resilience is characterized by three foundational pillars that can be actively developed and strengthened over time:</p>
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Flexibility (Mental)</h4>
                  <p className="text-sm">A mental-intellectual attribute that relates to cognitive adaptability and optimism. It represents the ability to remain open to new possibilities, reframe challenges, and apply critical thinking to overcome difficulties rather than becoming rigid.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Emotional Stability (Affective)</h4>
                  <p className="text-sm">An internal emotional state that is grounded and not easily swayed by external chaos. It enables individuals to act rationally, soothe their nervous system, and maintain a calm presence even in high-pressure situations.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Self-Efficacy (Motivational)</h4>
                  <p className="text-sm">A directional-motivational aspect measuring deep-seated self-confidence. It predicts an individual's willingness to take on complex challenges, learn new skills, and persistently drive toward goals despite setbacks.</p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-serif text-ink mb-4">The Resilience Cycle</h3>
            <p className="mb-8">Resilience is not merely the ability to "bounce back" to the status quo; it is the capacity to integrate difficult experiences and emerge stronger. The FIVE LENS model measures how effectively individuals navigate the three stages of the resilience cycle:</p>

            <div className="space-y-6 mb-12">
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">1. React (Shock & Absorption)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">How an individual absorbs the initial impact of a stressor. High resilience in this stage involves recognizing the emotional toll without being completely overwhelmed or immediately resorting to destructive Enneagram defense mechanisms.</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">2. Recover (Regulation)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">The process of finding equilibrium. This involves emotional regulation, seeking appropriate support, and using cognitive flexibility to assess the reality of the situation rather than catastrophizing.</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="md:w-1/3 font-bold text-rule-3">3. Adapt (Integration & Growth)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Translating the experience into learning. High resilience culminates in self-efficacy, where the individual uses the adversity to build new capabilities and approach future challenges with increased confidence.</p></div>
              </div>
            </div>


            <SocialShare title={`FIVE LENS Framework: ${t('framework.lens3.title', 'Emotional Resilience')}`} />
          </div>
        </motion.div>
      </div>
    </main>
  );
}
